import { useInfiniteCandidateJobsQuery } from '@/modules/candidates/queries/useCandidateJobsQuery';
import type {
  CandidateJobApi,
  CandidateOriginApi,
} from '@/modules/candidates/types';

/** Where a candidate sits from the customer's point of view. */
export type DecisionGroup = 'waiting' | 'interviewing' | 'hired' | 'passed';

/** Pre-introduction pipeline, read-only for the customer. */
export type ProgressStage =
  | 'sourced'
  | 'shortlisted'
  | 'outreach'
  | 'prescreening';

export type CandidateStage = ProgressStage | DecisionGroup;

export type CandidateFilter = 'all' | 'inProgress' | DecisionGroup;

/** The stages a filter shows, in pipeline order. */
export const activeStagesByFilter: Record<CandidateFilter, CandidateStage[]> = {
  all: [
    'sourced',
    'shortlisted',
    'outreach',
    'prescreening',
    'waiting',
    'interviewing',
    'hired',
    'passed',
  ],
  inProgress: ['sourced', 'shortlisted', 'outreach', 'prescreening'],
  waiting: ['waiting'],
  interviewing: ['interviewing'],
  hired: ['hired'],
  passed: ['passed'],
};

export interface CandidateStageQuery {
  candidates: CandidateJobApi[] | undefined;
  hasNextPage: boolean;
  isError: boolean;
  isFetching: boolean;
  isFetchingNextPage: boolean;
  loadMore: () => void;
  refetch: () => void;
  totalCount: number | undefined;
}

type InfiniteCandidateJobsQuery = ReturnType<
  typeof useInfiniteCandidateJobsQuery
>;

const toStageQuery = (
  query: InfiniteCandidateJobsQuery,
): CandidateStageQuery => ({
  candidates: query.data === undefined ? undefined : query.candidates,
  hasNextPage: query.hasNextPage === true,
  isError: query.isError,
  isFetching: query.isFetching,
  isFetchingNextPage: query.isFetchingNextPage,
  loadMore: () => {
    if (query.hasNextPage) {
      void query.fetchNextPage();
    }
  },
  refetch: () => {
    void query.refetch();
  },
  totalCount: query.totalCount,
});

/**
 * Candidate pages for each portal stage. Each stage keeps its own cursor so a
 * large sourced queue cannot delay candidates in later stages. Rejections
 * before introduction and Declined stay hidden; the API's candidate counts
 * apply the same rule, so keep them in step. `search` narrows every stage to
 * names and emails that match, `origin` to one Candidate Origin.
 */
export function useCandidatePipelineQuery(
  jobId: string | undefined,
  filter: CandidateFilter,
  search: string,
  origin: CandidateOriginApi | undefined,
) {
  const activeStages = activeStagesByFilter[filter];
  const isActive = (stage: CandidateStage) => activeStages.includes(stage);
  const shared = { jobId, search: search.trim() || undefined, origin };

  const sourcedQuery = useInfiniteCandidateJobsQuery({
    enabled: isActive('sourced'),
    ...shared,
    status: ['New'],
  });
  const shortlistedQuery = useInfiniteCandidateJobsQuery({
    enabled: isActive('shortlisted'),
    ...shared,
    status: ['Shortlisted'],
  });
  const outreachQuery = useInfiniteCandidateJobsQuery({
    enabled: isActive('outreach'),
    ...shared,
    status: ['ContactStarted', 'Contacted'],
  });
  const prescreeningQuery = useInfiniteCandidateJobsQuery({
    enabled: isActive('prescreening'),
    ...shared,
    status: [
      'PrescreeningScheduled',
      'Prescreened',
      'AiPrescreened',
      'AssessmentCompleted',
      'ReadyForSubmission',
    ],
  });
  const waitingQuery = useInfiniteCandidateJobsQuery({
    enabled: isActive('waiting'),
    ...shared,
    status: ['Submitted'],
  });
  const interviewingQuery = useInfiniteCandidateJobsQuery({
    enabled: isActive('interviewing'),
    ...shared,
    // Approved candidates were invited to a first-round interview; one tab
    // keeps the pipeline short, and the row chip tells them apart.
    status: ['HiringManagerApproved', 'Interviewing'],
  });
  const hiredQuery = useInfiniteCandidateJobsQuery({
    enabled: isActive('hired'),
    ...shared,
    status: ['Hired'],
  });
  const passedQuery = useInfiniteCandidateJobsQuery({
    enabled: isActive('passed'),
    ...shared,
    status: ['Rejected'],
    transitionFrom: ['Submitted', 'HiringManagerApproved'],
    transitionTo: 'Rejected',
  });

  const stages = {
    sourced: toStageQuery(sourcedQuery),
    shortlisted: toStageQuery(shortlistedQuery),
    outreach: toStageQuery(outreachQuery),
    prescreening: toStageQuery(prescreeningQuery),
    waiting: toStageQuery(waitingQuery),
    interviewing: toStageQuery(interviewingQuery),
    hired: toStageQuery(hiredQuery),
    passed: toStageQuery(passedQuery),
  } satisfies Record<CandidateStage, CandidateStageQuery>;
  return { stages };
}
